/**
 * TeamSystem Impact - OAuth Client API Client
 * Lista e revoca client OAuth registrati
 */

import axios from 'axios';

export interface OAuthClientDTO {
  _id: string;
  client_id: string;
  client_name: string;
  redirect_uris: string[];
  grant_types: string[];
  createdAt: string;
  updatedAt: string;
}

export interface OAuthSessionDTO {
  client_id: string;
  client_name: string;
  userId: string;
  userName: string;
  userEmail: string;
  scopes: string[];
  createdAt: string;
  expiresAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// OAuth routes are registered at root (no /api prefix) for RFC compliance,
// so we use a dedicated axios instance instead of apiClient (which prepends /api).
const oauthClient = axios.create({ baseURL: '' });

// Forward the JWT token from Zustand-persisted localStorage
oauthClient.interceptors.request.use((config) => {
  try {
    const stored = localStorage.getItem('teamsystem-impact-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      const token = parsed.token || null;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const aziendaId = parsed.utente?.azienda?.id;
      if (aziendaId) {
        config.headers['x-azienda-id'] = aziendaId;
      }
    }
  } catch {
    // ignore parse errors
  }
  return config;
});

const BASE_PATH = '/oauth/clients';

export const oauthClientApi = {
  list: async (): Promise<OAuthClientDTO[]> => {
    const response = await oauthClient.get<ApiResponse<OAuthClientDTO[]>>(BASE_PATH);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list OAuth clients');
    return response.data.data!;
  },

  revoke: async (clientId: string): Promise<void> => {
    const response = await oauthClient.delete<ApiResponse<void>>(`${BASE_PATH}/${clientId}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to revoke OAuth client');
  },

  listSessions: async (): Promise<OAuthSessionDTO[]> => {
    const response = await oauthClient.get<ApiResponse<OAuthSessionDTO[]>>('/oauth/sessions');
    if (!response.data.success) throw new Error(response.data.error || 'Failed to list OAuth sessions');
    return response.data.data!;
  },
};
